import {useState} from 'react';
import {useMutation} from '@apollo/client';
import {useSnackbar} from 'notistack';
import {useNavigate} from 'react-router-dom';
import {useAccountContext} from '../../contexts/AccountContext';
import {BankDetailsEnum} from '../../schema-types/globalTypes';
import {
  UpsertBankAccount,
  UpsertBankAccountVariables,
} from '../../schema-types/UpsertBankAccount';
import {
  SubmitBankAccount,
  SubmitBankAccountVariables,
} from '../../schema-types/SubmitBankAccount';
import validateUUID from '../../utils/validateUUID';
import OWNERSHIP from '../../constants/Ownership';
import {SUBMIT_BANK_ACCOUNT, UPSERT_BANK_ACCOUNT} from './bankAccountQueries';
import {bankAccountValues} from './bankAccountFields';

// The backend `status` is free text; these values mirror BankDetailsEnum.
const STATUS_NOT_KNOWN = 'Not known';
const STATUS_NEED_VALIDATING = 'Need validating';

export default function useSaveBankAccount() {
  const {accountId} = useAccountContext();
  const navigate = useNavigate();
  const {enqueueSnackbar} = useSnackbar();
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [upsertBankAccount] = useMutation<UpsertBankAccount, UpsertBankAccountVariables>(
    UPSERT_BANK_ACCOUNT,
    {
      // Operation name of GET_BANK_ACCOUNTS, so the overview grid refreshes.
      refetchQueries: ['BankAccounts'],
      awaitRefetchQueries: true,
    }
  );
  const [submitBankAccount] = useMutation<SubmitBankAccount, SubmitBankAccountVariables>(
    SUBMIT_BANK_ACCOUNT,
    {
      // Refetch again after submission so the grid shows the post-submit status.
      refetchQueries: ['BankAccounts'],
      awaitRefetchQueries: true,
    }
  );

  const submit = async (bankAccountId: string) => {
    setSubmitting(true);
    try {
      const {data} = await submitBankAccount({
        variables: {bankAccountId: validateUUID(bankAccountId)},
      });
      if (!data?.submitBankAccount?.ok) {
        // The record is already saved; the backend flags it as a failed
        // submission, so it is safe to leave the page.
        enqueueSnackbar(
          `Bank account saved but could not be submitted: ${
            data?.submitBankAccount?.errors?.join(', ') || 'unknown error'
          }`,
          {variant: 'error'}
        );
        return;
      }
      enqueueSnackbar('Bank account submitted.', {variant: 'success'});
    } finally {
      setSubmitting(false);
    }
  };

  const save = async (values: bankAccountValues) => {
    const isNotKnown = values.bankDetails === BankDetailsEnum.NOT_KNOWN;
    setSaving(true);
    try {
      const {data} = await upsertBankAccount({
        variables: {
          accountId: validateUUID(accountId),
          // A bank account without a contact renders as "Joint" in the grid.
          contactId:
            values.contactId === OWNERSHIP.JOINT_CODE
              ? undefined
              : validateUUID(values.contactId),
          providerId: validateUUID(values.providerId),
          accountName: values.accountName,
          accountNumber: isNotKnown ? undefined : values.accountNumber,
          sortCode: isNotKnown ? undefined : values.sortCode,
          status: isNotKnown ? STATUS_NOT_KNOWN : STATUS_NEED_VALIDATING,
        },
      });
      if (!data?.upsertBankAccount?.ok) {
        enqueueSnackbar(
          `Could not save bank account: ${
            data?.upsertBankAccount?.errors?.join(', ') || 'unknown error'
          }`,
          {variant: 'error'}
        );
        return;
      }
      enqueueSnackbar('Saved bank account.', {variant: 'success'});
      const bankAccountId = data.upsertBankAccount.bankAccount?.id;
      if (bankAccountId) {
        await submit(bankAccountId);
      } else {
        // Defensive: ok=true should always carry the saved bank account.
        enqueueSnackbar('Bank account saved but could not be submitted.', {
          variant: 'error',
        });
      }
      navigate({pathname: `/account/${accountId}`});
    } finally {
      setSaving(false);
    }
  };

  return {save, saving, submitting};
}
